import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Stack } from '@mui/material';
import { Solution } from '../types';
import { Description as FileIcon } from '@mui/icons-material';

interface SolutionCardProps {
  solution: Solution;
  onClick?: (solution: Solution) => void;
}

const SolutionCard: React.FC<SolutionCardProps> = ({ solution, onClick }) => {
  return (
    <Card
      onClick={() => onClick?.(solution)}
      sx={{
        minWidth: 250,
        maxWidth: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: solution.is_public ? '2px solid' : '1px solid',
        borderColor: solution.is_public ? 'rgba(33, 150, 243, 0.6)' : 'divider',
        backgroundColor: solution.is_public ? 'rgba(33, 150, 243, 0.04)' : 'inherit',
        transition: 'all 0.2s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
        {/* Thumbnail */}
        {solution.pattern_thumbnail_url ? (
          <Box
            component="img"
            src={solution.pattern_thumbnail_url}
            alt="Miniature de la solution"
            sx={{
              width: '100%',
              height: 120,
              objectFit: 'contain',
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <FileIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          </Box>
        )}

        {/* Description (latex_template preview) */}
        {solution.latex_template && solution.latex_template.trim() && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom>
              Description
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                fontSize: '0.813rem',
                lineHeight: 1.4,
              }}
            >
              {solution.latex_template.substring(0, 150)}
              {solution.latex_template.length > 150 && '...'}
            </Typography>
          </Box>
        )}

        {/* Skills */}
        {solution.skills && solution.skills.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom>
              Savoir-faire
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {solution.skills.map((skill) => (
                <Chip
                  key={skill.id}
                  label={skill.name}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: 'primary.50',
                    color: 'primary.700',
                    border: '1px solid',
                    borderColor: 'primary.200',
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Tags */}
        {solution.tags && solution.tags.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom>
              Tags
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {solution.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SolutionCard;
